import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useBuilder } from '../lib/builderStore';
import type { Condition, LogicRule } from '../lib/types';
import {
  Button, Card, EmptyState, Field, IconButton, Select, Spinner,
} from '../components/ui';

const OPERATORS = [
  { op: 'eq', label: 'is' },
  { op: 'ne', label: 'is not' },
  { op: 'gt', label: 'is greater than' },
  { op: 'gte', label: 'is at least' },
  { op: 'lt', label: 'is less than' },
  { op: 'lte', label: 'is at most' },
  { op: 'contains', label: 'contains' },
  { op: 'isAnswered', label: 'is answered' },
  { op: 'isEmpty', label: 'is empty' },
];

const ACTIONS = [
  { type: 'show', label: 'Show' },
  { type: 'hide', label: 'Hide' },
  { type: 'setRequired', label: 'Make required' },
  { type: 'jumpTo', label: 'Jump to page' },
  { type: 'screenOut', label: 'Screen out' },
];

export function Logic() {
  const { formId = '' } = useParams();

  const definition = useBuilder((s) => s.definition);
  const loadedFormId = useBuilder((s) => s.formId);
  const load = useBuilder((s) => s.load);
  const apply = useBuilder((s) => s.apply);
  const dirty = useBuilder((s) => s.dirty);
  const markSaved = useBuilder((s) => s.markSaved);

  const draft = useQuery({
    queryKey: ['definition', formId],
    queryFn: () => api.forms.getDefinition(formId),
    enabled: Boolean(formId),
  });

  useEffect(() => {
    if (draft.data && loadedFormId !== formId) load(formId, draft.data);
  }, [draft.data, formId, loadedFormId, load]);

  useEffect(() => {
    if (!dirty || !definition) return;
    const timer = setTimeout(async () => {
      await api.forms.saveDefinition(formId, definition);
      markSaved();
    }, 1200);
    return () => clearTimeout(timer);
  }, [dirty, definition, formId, markSaved]);

  if (!definition) return <div className="p-8"><Spinner /></div>;

  const questions = definition.pages.flatMap((page) =>
    page.elements
      .filter((e) => e.kind === 'question')
      .map((e) => ({ ref: e.ref, title: e.title ?? e.ref, page: page.ref })),
  );

  const addRule = () => apply('Add logic rule', (draft) => {
    const first = questions[0];
    draft.logic.push({
      ref: `rule_${draft.logic.length + 1}`,
      trigger: 'onChange',
      when: { op: 'eq', left: { ref: first?.ref ?? '' }, right: '' },
      actions: [{ type: 'show', target: { question: questions[1]?.ref ?? '' } }],
      reversible: true,
    } as LogicRule);
  });

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-lg font-semibold">Logic</h1>
          <p className="text-sm text-[var(--ff-text-muted)]">
            Rules are evaluated as a dependency graph, not top to bottom — reordering them
            never changes the result.
          </p>
        </div>
        <Button className="ml-auto" variant="primary"
                onClick={addRule} disabled={questions.length < 2}>
          Add rule
        </Button>
      </div>

      {questions.length < 2 && (
        <Card className="p-4 text-sm text-[var(--ff-text-muted)]">
          Add at least two questions before writing logic.
        </Card>
      )}

      {definition.logic.length === 0 && questions.length >= 2 && (
        <Card>
          <EmptyState
            title="No logic yet"
            description="Show or hide questions based on earlier answers, branch between pages, or screen respondents out."
            action={<Button variant="primary" onClick={addRule}>Add your first rule</Button>}
          />
        </Card>
      )}

      {definition.logic.map((rule, index) => (
        <RuleCard key={rule.ref} rule={rule} index={index}
                  questions={questions} pages={definition.pages.map((p) => p.ref)} />
      ))}
    </div>
  );
}

function RuleCard({
  rule, index, questions, pages,
}: {
  rule: LogicRule;
  index: number;
  questions: { ref: string; title: string }[];
  pages: string[];
}) {
  const apply = useBuilder((s) => s.apply);

  const editRule = (label: string, mutate: (r: LogicRule) => void) =>
    apply(label, (draft) => {
      const target = draft.logic[index];
      if (target) mutate(target);
    });

  const condition: Condition = rule.when ?? { op: 'eq', left: { ref: '' }, right: '' };
  const needsValue = !['isAnswered', 'isEmpty'].includes(condition.op);
  const action = rule.actions[0];

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2">
        <code className="text-xs text-[var(--ff-text-muted)]">{rule.ref}</code>
        <IconButton
          label={`Delete rule ${rule.ref}`}
          className="ml-auto"
          onClick={() => apply('Delete rule', (d) => { d.logic.splice(index, 1); })}
        >
          ×
        </IconButton>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-end gap-2">
          <span className="pb-2 text-xs font-semibold uppercase text-[var(--ff-text-muted)]">
            When
          </span>
          <div className="min-w-40 flex-1">
            <Field label="Question">
              <Select
                value={condition.left?.ref ?? ''}
                onChange={(e) => editRule('Change condition target', (r) => {
                  r.when = { ...condition, left: { ref: e.target.value } };
                })}
              >
                {questions.map((q) => (
                  <option key={q.ref} value={q.ref}>{q.title}</option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-40">
            <Field label="Operator">
              <Select
                value={condition.op}
                onChange={(e) => editRule('Change operator', (r) => {
                  r.when = { ...condition, op: e.target.value };
                })}
              >
                {OPERATORS.map((o) => <option key={o.op} value={o.op}>{o.label}</option>)}
              </Select>
            </Field>
          </div>
          {needsValue && (
            <div className="w-32">
              <Field label="Value">
                <input
                  value={String(condition.right ?? '')}
                  onChange={(e) => editRule('Change condition value', (r) => {
                    const raw = e.target.value;
                    r.when = {
                      ...condition,
                      right: /^-?\d+(\.\d+)?$/.test(raw) ? Number(raw) : raw,
                    };
                  })}
                  className="w-full rounded-md border border-[var(--ff-border-strong)]
                             bg-transparent px-3 py-2 text-sm"
                />
              </Field>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <span className="pb-2 text-xs font-semibold uppercase text-[var(--ff-text-muted)]">
            Then
          </span>
          <div className="w-40">
            <Field label="Action">
              <Select
                value={action?.type ?? 'show'}
                onChange={(e) => editRule('Change action', (r) => {
                  const type = e.target.value as LogicRule['actions'][number]['type'];
                  r.actions = [{
                    type,
                    target: type === 'jumpTo'
                      ? { page: pages[pages.length - 1] }
                      : { question: action?.target?.question ?? questions[0]?.ref },
                  }];
                })}
              >
                {ACTIONS.map((a) => <option key={a.type} value={a.type}>{a.label}</option>)}
              </Select>
            </Field>
          </div>
          <div className="min-w-40 flex-1">
            <Field label={action?.type === 'jumpTo' ? 'Page' : 'Question'}>
              <Select
                value={action?.target?.page ?? action?.target?.question ?? ''}
                onChange={(e) => editRule('Change action target', (r) => {
                  const first = r.actions[0];
                  if (!first) return;
                  first.target = first.type === 'jumpTo'
                    ? { page: e.target.value }
                    : { question: e.target.value };
                })}
              >
                {(action?.type === 'jumpTo' ? pages : questions.map((q) => q.ref)).map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </Select>
            </Field>
          </div>
        </div>
      </div>

      <p className="mt-3 text-[11px] text-[var(--ff-text-muted)]">
        Enforced on the server as well as in the browser: an answer to a question this rule
        hides is rejected at submission.
      </p>
    </Card>
  );
}
